import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from './Logo';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  // Footer link columns based on PDF design
  const footerColumns = {
    company: {
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'FAQs', href: '/faqs' },
        { name: 'Contact', href: '/contact' },
      ]
    },
    quickLinks: {
      title: 'Quick Links',
      links: [
        { name: 'How It Works', href: '/how-it-works' },
        { name: 'Pricing', href: '/pricing' },
        { name: 'Features', href: '/features' },
      ]
    },
    legal: {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '/privacy-policy' },
        { name: 'Terms of Service', href: '/terms-of-service' },
        { name: 'Cookie Policy', href: '/cookie-policy' },
      ]
    }
  };

  const socialLinks = [
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/therealmasterji/',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      )
    },
    {
      name: 'LinkedIn',
      href: 'https://linkedin.com',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      )
    },
    {
      name: 'Twitter',
      href: 'https://twitter.com',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      )
    },
    {
      name: 'Email',
      href: 'mailto:infotherealmasterji@gmail.com',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  return (
    <footer className="bg-brand-blue text-white w-full">
      {/* Main Footer Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-12">
          {/* Brand Column */}
          <div className="flex-shrink-0">
            <Link to="/" onClick={handleLinkClick} className="flex items-center space-x-3 mb-4">
              <div style={{ width: '45px', height: '42px' }} className="bg-white rounded p-1">
                <Logo size="md" />
              </div>
            </Link>
            <p 
              className="mb-6 max-w-[248px]"
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: '400',
                color: '#FFFFFF',
              }}
            >
              Master Ji<br />Stitch. Manage. Grow.
            </p>
          </div>

          {/* Navigation Columns Container */}
          <div className="flex flex-wrap lg:flex-nowrap gap-12 lg:gap-16">
            {/* Company Column */}
            <div>
              <h4 
                className="mb-6"
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: '18px',
                  lineHeight: '28px',
                  fontWeight: '600',
                  color: '#FFFFFF'
                }}
              >
                {footerColumns.company.title}
              </h4>
              <ul className="space-y-2">
                {footerColumns.company.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      onClick={handleLinkClick}
                      className="transition-colors duration-200 hover:opacity-80"
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px',
                        fontWeight: '400',
                        color: '#FFFFFF'
                      }}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links Column */}
            <div>
              <h4 
                className="mb-6"
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: '18px',
                  lineHeight: '28px',
                  fontWeight: '600',
                  color: '#FFFFFF'
                }}
              >
                {footerColumns.quickLinks.title}
              </h4>
              <ul className="space-y-2">
                {footerColumns.quickLinks.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      onClick={handleLinkClick}
                      className="transition-colors duration-200 hover:opacity-80"
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px',
                        fontWeight: '400',
                        color: '#FFFFFF'
                      }}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h4 
                className="mb-6"
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: '18px',
                  lineHeight: '28px',
                  fontWeight: '600',
                  color: '#FFFFFF'
                }}
              >
                {footerColumns.legal.title}
              </h4>
              <ul className="space-y-2">
                {footerColumns.legal.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      onClick={handleLinkClick}
                      className="transition-colors duration-200 hover:opacity-80"
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px',
                        fontWeight: '400',
                        color: '#FFFFFF'
                      }}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Keep in Touch Column */}
            <div>
              <h4 
                className="mb-6"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '20px',
                  lineHeight: '30px',
                  fontWeight: '700',
                  color: '#FFFFFF',
                }}
              >
                Keep in Touch
              </h4>
              <div className="flex items-center space-x-4">
                {socialLinks.slice(0, 2).map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    target={social.href.startsWith('mailto') ? undefined : '_blank'}
                    rel={social.href.startsWith('mailto') ? undefined : 'noopener noreferrer'}
                    className="flex items-center justify-center text-white transition-all duration-200"
                    style={{ width: '24px', height: '24px' }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.name}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6" style={{ paddingLeft: '128px', paddingRight: '128px', paddingTop: '33px' }}>
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <p 
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: '400',
                color: '#FFFFFF',
                textAlign: 'center',
                width: '234px',
                height: '20px'
              }}
            >
              © {currentYear} Master Ji. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
